import dayjs from "dayjs";
import { GetSchedule, GetSlot } from "@/services/fhir";
import { TELECONSULTORIA_SERVICE_CATEGORY_TOKEN } from "../../constants";
import { organizationIdFromReference } from "../../services/resolveOrganizationDisplays";
import {
  healthcareServicesFromBundle,
  includedHealthcareService,
  schedulesFromBundle,
} from "../../services/teleconsultoriaScheduleService";
import type { TeleconsultoriaActor } from "../../types";
import { actorByType, isTeleconsultoriaAgendaSchedule } from "../../utils/actors";

/** Quantos dias à frente a afiliada enxerga os horários livres. */
export const PROVIDER_AGENDA_DAYS_AHEAD = 7;
const SCHEDULE_SEARCH_COUNT = 100;

/** Agenda de teleconsultoria COM horários da prestadora (o que é preciso para marcar). */
export interface ProviderAgenda {
  scheduleId: string;
  teleconsultor: TeleconsultoriaActor;
  location: TeleconsultoriaActor;
  healthcareService?: TeleconsultoriaActor;
}

export interface ProviderAgendaSlot {
  slotId: string;
  start: string;
  end?: string;
  professionalName: string;
  serviceName: string;
  agenda: ProviderAgenda;
}

function toProviderAgenda(schedule: fhir5.Schedule): ProviderAgenda | undefined {
  const teleconsultor = actorByType(schedule.actor, "Practitioner");
  const location = actorByType(schedule.actor, "Location");
  if (!schedule.id || !teleconsultor || !location) return undefined;
  const healthcareService = actorByType(schedule.actor, "HealthcareService");
  return {
    scheduleId: schedule.id,
    teleconsultor,
    location,
    ...(healthcareService ? { healthcareService } : {}),
  };
}

/**
 * Agendas de teleconsultoria COM horários, SÓ da prestadora.
 * Confere a organização pelo HCS incluído (providedBy): sem prova, descarta.
 */
export async function listProviderAgendas(providerToken: string): Promise<ProviderAgenda[]> {
  if (!providerToken) return [];
  const bundle = await GetSchedule({
    organization: providerToken,
    serviceCategory: TELECONSULTORIA_SERVICE_CATEGORY_TOKEN,
    active: true,
    includeHealthcareService: true,
    count: SCHEDULE_SEARCH_COUNT,
  });
  const services = healthcareServicesFromBundle(bundle);
  return schedulesFromBundle(bundle)
    .filter((schedule) => {
      if (!isTeleconsultoriaAgendaSchedule(schedule) || schedule.active === false) return false;
      const service = includedHealthcareService(schedule, services);
      return organizationIdFromReference(service?.providedBy?.reference) === providerToken;
    })
    .map(toProviderAgenda)
    .filter((agenda): agenda is ProviderAgenda => Boolean(agenda));
}

/** Agenda com horários de um teleconsultor da prestadora (modo "Agora"). */
export async function findTeleconsultorAgenda(params: {
  providerToken: string;
  practitionerReference: string;
}): Promise<ProviderAgenda | undefined> {
  const agendas = await listProviderAgendas(params.providerToken);
  return agendas.find(
    (agenda) => agenda.teleconsultor.reference === params.practitionerReference,
  );
}

/** Início da busca: o começo do período, mas nunca antes de agora (horário passado não se marca). */
function searchStart(startDate?: string): string {
  const now = dayjs();
  if (!startDate) return now.toISOString();
  const start = dayjs(startDate).startOf("day");
  return (start.isAfter(now) ? start : now).toISOString();
}

/**
 * Horários livres (slots `free`, gerados pelo backend) das agendas da prestadora.
 * Período opcional (YYYY-MM-DD); sem ele, de agora até PROVIDER_AGENDA_DAYS_AHEAD dias.
 * Ordenados por início.
 */
export async function listProviderAgendaSlots(
  providerToken: string,
  range?: { startDate: string; endDate: string },
): Promise<ProviderAgendaSlot[]> {
  // Período todo no passado: nada a marcar (evita a busca).
  if (range && dayjs(range.endDate).endOf("day").isBefore(dayjs())) return [];
  const agendas = await listProviderAgendas(providerToken);
  if (!agendas.length) return [];

  const agendaById = new Map(agendas.map((agenda) => [agenda.scheduleId, agenda]));
  const bundle: fhir5.Bundle | undefined = await GetSlot({
    schedule: agendas.map((agenda) => agenda.scheduleId).join(","),
    status: ["free"],
    startDate: searchStart(range?.startDate),
    endDate: range?.endDate || dayjs().add(PROVIDER_AGENDA_DAYS_AHEAD, "day").format("YYYY-MM-DD"),
    sort: "start",
  });

  return (bundle?.entry || [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.Slot => resource?.resourceType === "Slot")
    .map((slot): ProviderAgendaSlot | undefined => {
      const agenda = agendaById.get(slot.schedule?.reference?.split("/")[1] || "");
      if (!slot.id || !slot.start || !agenda) return undefined;
      return {
        slotId: slot.id,
        start: slot.start,
        end: slot.end,
        professionalName: agenda.teleconsultor.display || "Teleconsultor",
        serviceName: agenda.healthcareService?.display || "",
        agenda,
      };
    })
    .filter((slot): slot is ProviderAgendaSlot => Boolean(slot));
}
